import { useCallback, useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { adminClientsKeys } from 'store/admin-clients';
import { useClientAdmin } from 'store/admin-client-admins';
import { usePredefinedToasts } from 'utils/Toast';
import { makeRequest } from '../api/api';
import { useLoading } from '../utils/Loading';
import {
  LocalPermissionsRequest,
  UsePermissionsParams,
} from '../types/Administrators';
import { usePermissionUtils } from '../utils/Permissions';
import { Endpoints } from './../api/constants';
import { ImportClientsResponse } from './../types/Clients';

export const useClient = (clientId?: string) => {
  const queryClient = useQueryClient();
  const predefinedToasts = usePredefinedToasts();
  const url = !!clientId
    ? Endpoints.clientAdmin.clients.byId.root.replace(':clientId', clientId)
    : null;

  const { loading, startLoading, stopLoading } = useLoading();

  const importClients = async (file: File) => {
    startLoading();
    const formData = new FormData();
    formData.append('file', file);
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    const data = await makeRequest(
      'post',
      `${url}/importEmployees`,
      formData,
      headers,
    )
      .then((res) => {
        const response = res.data as ImportClientsResponse;
        const noCriticalErrors = response.errors.every(
          (error) => error.severity !== 'critical',
        );
        if (noCriticalErrors) {
          predefinedToasts.success.added();
          queryClient.invalidateQueries(adminClientsKeys.all);
        }

        return response;
      })
      .finally(() => {
        stopLoading();
      });
    return data;
  };
  return {
    loading,
    importClients,
  };
};

export const useClientPermissions = ({
  clientId,
  adminId,
  selectedId,
  removeChanges,
}: UsePermissionsParams) => {
  const { startLoading, stopLoading, loading: apiLoading } = useLoading();
  const { t } = useTranslation();
  const { data: clientAdmin } = useClientAdmin(clientId ?? '', adminId);

  const {
    wasChanged,
    parsePermissionEntry,
    getAllLocalPermissions,
    getCompanyGroupPermissions,
    getCompanyPermissions,
    getParsedPermissions,
  } = usePermissionUtils();

  const clientAdminIsRoot = !!clientAdmin?.root;

  const companyId = useMemo(() => {
    if (wasChanged) return false;
    if (selectedId.includes('company-'))
      return selectedId.replace('company-', '');
    return false;
  }, [selectedId, wasChanged]);

  const companyGroupId = useMemo(() => {
    if (wasChanged) return false;
    if (selectedId.includes('companyGroup-'))
      return selectedId.replace('companyGroup-', '');
    return false;
  }, [selectedId, wasChanged]);

  const basePermissionUrl = useMemo(
    () =>
      `${Endpoints.clientAdmin.clients.root}/${clientId}/admins/${adminId}/permissions`,
    [adminId, clientId],
  );

  const companyGroupUrl = useMemo(
    () =>
      companyGroupId
        ? `${basePermissionUrl}/companyGroup/${companyGroupId}`
        : null,
    [basePermissionUrl, companyGroupId],
  );

  const companyUrl = useMemo(() => {
    if (selectedId === 'allLocalPermissions')
      return `${basePermissionUrl}/company/`;
    return companyId ? `${basePermissionUrl}/company/${companyId}` : null;
  }, [basePermissionUrl, companyId, selectedId]);

  const globalPermissionUrl = useMemo(
    () =>
      adminId && !companyGroupId && !companyId && selectedId
        ? `${basePermissionUrl}/global`
        : null,
    [adminId, basePermissionUrl, companyGroupId, companyId, selectedId],
  );

  const { data: globalPermissions, error: globalPermissionError } =
    useSWR(globalPermissionUrl);

  const { data: companyPermissions, error: companyError } = useSWR(companyUrl);

  const { data: companyGroupPermissions, error: companyGroupError } =
    useSWR(companyGroupUrl);

  const globalPermissionsLoading = useMemo(
    () => !globalPermissions && !globalPermissionError,
    [globalPermissionError, globalPermissions],
  );

  const companyPermissionsLoading = useMemo(
    () => !companyPermissions && !companyError,
    [companyError, companyPermissions],
  );

  const companyGroupPermissionsLoading = useMemo(
    () => !companyGroupPermissions && !companyGroupError,
    [companyGroupError, companyGroupPermissions],
  );

  const loading = useMemo(() => {
    if (!selectedId) return false;
    if (!!companyGroupId) return companyGroupPermissionsLoading;
    if (!!companyId) return companyPermissionsLoading;

    return globalPermissionsLoading;
  }, [
    companyGroupId,
    companyGroupPermissionsLoading,
    companyId,
    companyPermissionsLoading,
    globalPermissionsLoading,
    selectedId,
  ]);

  const parsedGlobalPermissions = useMemo(
    () => getParsedPermissions(globalPermissions, clientAdminIsRoot),
    [getParsedPermissions, globalPermissions, clientAdminIsRoot],
  );

  const parsedCompanyPermissions = useMemo(
    () => getParsedPermissions(companyPermissions, clientAdminIsRoot),
    [companyPermissions, getParsedPermissions, clientAdminIsRoot],
  );

  const parsedCompanyGroupPermissions = useMemo(
    () => getParsedPermissions(companyGroupPermissions, clientAdminIsRoot),
    [companyGroupPermissions, getParsedPermissions, clientAdminIsRoot],
  );

  const updateGlobalPermissions = useCallback(
    async (globalEntries: [string, any] | undefined) => {
      if (!globalEntries) return;
      const requestData = parsePermissionEntry([globalEntries]);
      if (requestData.length > 0) {
        await makeRequest('patch', `${basePermissionUrl}/global`, {
          updatedModules: requestData,
        });
        mutate(`${basePermissionUrl}/global`);
      }
    },
    [basePermissionUrl, parsePermissionEntry],
  );

  const updateLocalPermissions = useCallback(
    async (requestData: LocalPermissionsRequest) => {
      if (requestData.updatedModules.length > 0) {
        await makeRequest('patch', `${basePermissionUrl}/local`, requestData);
        mutate(`${basePermissionUrl}/local`);
      }
    },
    [basePermissionUrl],
  );

  const updatePermissions = useCallback(
    async (data: FormData) => {
      startLoading();
      const entries = Object.entries(data);
      const globalEntries = entries.find(
        ([key]) => key === 'allGlobalPermissions',
      );
      await updateGlobalPermissions(globalEntries);

      const localEntries = entries.find(
        ([key]) => key === 'allLocalPermissions',
      );
      const companyGroupEntries = entries
        .filter(([key]) => key.includes('companyGroup-'))
        .map(([key, value]) => [key.replace('companyGroup-', ''), value]);
      const companyEntries = entries
        .filter(([key]) => key.includes('company-'))
        .map(([key, value]) => [key.replace('company-', ''), value]);
      const localPermissions = getAllLocalPermissions(localEntries);
      const companyGroupPermissions =
        getCompanyGroupPermissions(companyGroupEntries);
      const companyPermissions = getCompanyPermissions(companyEntries);
      const requestData: LocalPermissionsRequest = {
        updatedModules: [
          ...companyPermissions,
          ...companyGroupPermissions,
          ...localPermissions,
        ],
      };
      await updateLocalPermissions(requestData);
      removeChanges();
      toast(t('common.updated_succesfully'), { type: 'success' });
      mutate(Endpoints.clientAdmin.companies.admins.me);
      stopLoading();
    },
    [
      startLoading,
      updateGlobalPermissions,
      getAllLocalPermissions,
      getCompanyGroupPermissions,
      getCompanyPermissions,
      updateLocalPermissions,
      removeChanges,
      t,
      stopLoading,
    ],
  );

  return {
    updatePermissions,
    globalPermissions,
    companyPermissions,
    companyGroupPermissions,
    loading: loading || apiLoading,
    companyGroupId,
    companyId,
    parsedGlobalPermissions,
    parsedCompanyPermissions,
    parsedCompanyGroupPermissions,
  };
};
